/*
 * Copyright (c) 2017-2019 by Gary Kumfert. All rights reserved.
 *
 * MIT License
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the "Software"), to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.*/

import {
    Directive,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    ChangeDetectorRef,
    HostListener,
    HostBinding,
    Renderer2,
} from '@angular/core';

import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';

import { DNDMode, DNDInfo, DNDService } from './dnd.service';
import { DNDContainerDirective } from './dnd.container.directive';

/*
<div dnd-container [sortableData]='myItemArray'>
  <div *ngFor='let item of myItemArray; let i = index'
       gk-dnd-item
       [sortableIndex]='i'
       [dragData]='myData'
       (onDropSuccess)='onDropSuccess($event)'
       (onDragOver)='onDragOver($event)'>

    <gk-playlistitem-editor [data]='item'></gk-playlistitem-editor>
  </div>
</div>
*/
@Directive({
    selector: '[gk-dnd-item]',
})
export class DNDItemDirective {

    private readonly DRAG: string = 'dnd-drag-continue';
    private readonly COPY: string = 'dnd-copy-continue';
    private readonly MOVE: string = 'dnd-move-continue';
    private readonly SORT: string = 'dnd-sort-continue';

    @HostBinding('draggable')
    get draggable(): boolean {
        return true;
    }

    @Input('sortableIndex') index: number = -1;

    /**
     * The data that has to be dragged. It can be any JS object
     */
    @Input() dragData: any;

    /**
     * Declares a set of types this item can be
     */
    @Input() declaredTypes: string[] = [];

    @Output() onDragStart: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();
    @Output() onDragContinue: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();
    @Output() onDragEnd: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();

    constructor(
        public el: ElementRef,
        public container: DNDContainerDirective,
        private dnd: DNDService,
        private renderer: Renderer2,
        private logger: NGXLogger,
    ) {
        // this.el.nativeElement.draggable = true;
        if (this.dnd.isDragging) {
            this.dnd.onNewDNDItem(this);
            this.logger.info('created');
        }
    }

    public decorateForMode(mode: DNDMode): void {
        switch (mode) {

            case DNDMode.COPY:
                this.addCssClasses([this.DRAG, this.COPY]);
                this.removeCssClasses([this.MOVE, this.SORT]);
                break;

            case DNDMode.MOVE:
                this.addCssClasses([this.DRAG, this.MOVE]);
                this.removeCssClasses([this.COPY, this.SORT]);
                break;

            case DNDMode.SORT:
                this.addCssClasses([this.DRAG, this.SORT]);
                this.removeCssClasses([this.COPY, this.MOVE]);
                break;

            case DNDMode.DROP:
            case DNDMode.DELETE:
            case DNDMode.CANCEL:
            case DNDMode.UNKNOWN:
            default:
                this.removeCssClasses([this.DRAG, this.COPY, this.MOVE, this.SORT]);
                break;
        }
    }

    //
    // These are the main events we respond to
    //

    // called once on mouseDown
    @HostListener('dragstart', ['$event']) _onDragStart(event: any): void {
        let dndInfo: DNDInfo = this.dnd.onDragStart(event, this);
        this.onDragStart.emit(dndInfo);
        // this.logger.log('dnd.item: onDragStart ', event, this);
    }

    // called on every refresh (not just mouse moves)
    @HostListener('drag', ['$event']) _onDragContinue(event: any): void {
        // this.renderer.addClass(this.el.nativeElement, 'dnd-drag-continue');
        let dndInfo: DNDInfo = this.dnd.onDragContinue(event, this);
        this.decorateForMode(dndInfo.mode);
        this.onDragContinue.emit(dndInfo);
        // this.logger.log('dnd.item: onDragContinue ', event, this);
    }

    // called on ever mouse up.
    @HostListener('dragend', ['$event']) _onDragEnd(event: any): void {
        // this.renderer.removeClass(this.el.nativeElement, 'dnd-drag-continue');
        let dndInfo: DNDInfo = this.dnd.onDragEnd(event, this);
        this.decorateForMode(dndInfo.mode);
        // higher level notifications
        if (dndInfo.mode === DNDMode.DELETE) {
            dndInfo.originalContainer.onDragDelete.emit(dndInfo);
        } else if (dndInfo.mode === DNDMode.DROP) {
            if (dndInfo.originalContainer === dndInfo.targetContainer) {
                dndInfo.targetContainer.onSortSuccess.emit(dndInfo);
            } else {
                dndInfo.targetContainer.onDropSuccess.emit(dndInfo);
            }
        }
        this.onDragEnd.emit(dndInfo);

        // this.logger.log('dnd.item: onDragEnd ', event, this);
    }

    // called when something else is dragged over (used for sorting)
    @HostListener('dragenter', ['$event']) _onDragEnter(event: any): void {
        if (!this.dnd.isDropAllowed(event, this.container)) { return; }
        this.dnd.onDragOverItem(event, this);
        // this.logger.log('did.item: onDragEnter', event, this);
    }

    private addCssClasses(properties: string[]): void {
        properties.forEach((property: string) => this.renderer.addClass(this.el.nativeElement, property));
    }

    private removeCssClasses(properties: string[]): void {
        properties.forEach((property: string) => this.renderer.removeClass(this.el.nativeElement, property));
    }
}
